// Unified card used by RTLSPanel for every entity type (personnel,
// equipment, location, access point, chamber). The same visual treatment
// — status-colored left border + 3 lines of text + hover-reveal pencil —
// applies to all five.
//
// The caller is responsible for mapping each domain's status enum into
// the normalized `statusKey` used here.

import React from 'react'
import clsx from 'clsx'

export type EntityKind =
  | 'personnel'
  | 'equipment'
  | 'location'
  | 'access_point'
  | 'chamber'

export type EntityStatusKey = 'active' | 'idle' | 'alert' | 'offline' | 'warn' | 'ok'

const BORDER_CLASS: Record<EntityStatusKey, string> = {
  active:  'border-l-statusGreen',
  ok:      'border-l-statusGreen',
  idle:    'border-l-statusBlue',
  warn:    'border-l-statusOrange',
  alert:   'border-l-statusRed',
  offline: 'border-l-textMuted',
}

const STATUS_TEXT_CLASS: Record<EntityStatusKey, string> = {
  active:  'text-statusGreen',
  ok:      'text-statusGreen',
  idle:    'text-statusBlue',
  warn:    'text-statusOrange',
  alert:   'text-statusRed',
  offline: 'text-textMuted',
}

export interface EntityCardProps {
  kind: EntityKind
  id: string
  /** Bold first-line identifier — typically the asset_id or human name. */
  primary: string
  /** Inline subtitle on line 1 (after a `·`) — subtype or parent location. */
  secondary?: string
  /** Second line — free-form metadata (Zone, level, occupancy, etc.). */
  meta?: string
  /** Third line — relative timestamp ("30s ago"). */
  timeAgo?: string
  /** Drives the left-border color + status label color. */
  statusKey: EntityStatusKey
  statusLabel?: string
  /** When true, adds a gold ring + tint atop the status border. */
  selected?: boolean
  /** Primary click — typically "fly to" or "select asset". */
  onClick: () => void
  /** When provided, a hover-reveal ✎ button appears. */
  onEdit?: () => void
}

const TYPE_TAG: Record<EntityKind, string> = {
  personnel:    'PR',
  equipment:    'EQ',
  location:     'LOC',
  access_point: 'AP',
  chamber:      'CH',
}

export const EntityCard = React.memo<EntityCardProps>(({
  kind,
  primary,
  secondary,
  meta,
  timeAgo,
  statusKey,
  statusLabel,
  selected = false,
  onClick,
  onEdit,
}) => {
  // Compact 2-line layout.
  //   left column  → primary + condensed subline
  //   right column → TAG + status (top-right, flush to the card edge)
  //                  pencil ✎ (bottom-right, hover-reveal — uses the
  //                  vertical space under TAG/status, not a full-height
  //                  column beside the content)
  const subline = [secondary, meta, timeAgo].filter(Boolean).join(' · ')

  // Card is a clickable div so we can nest a pencil <button> for edit
  // without violating HTML's no-button-in-button rule. Keyboard support
  // mirrors what a <button> would give: Enter / Space invoke onClick.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-pressed={selected}
      className={clsx(
        // Option-A surface hierarchy: resting = surfaceLight (#333), hover
        // jumps to surfaceBorder (#404) for clear elevation. Selected stays
        // at resting bg — the gold ring is the unambiguous signal.
        'group relative mx-md mb-xs flex cursor-pointer items-stretch gap-xs rounded-sm border border-l-[3px] border-surfaceBorder bg-surfaceLight px-sm py-[6px] transition-colors hover:bg-surfaceBorder focus:bg-surfaceBorder focus:outline-none',
        BORDER_CLASS[statusKey],
        selected && 'ring-1 ring-primary',
      )}
    >
      {/* Left column: primary + subline */}
      <div className="flex min-w-0 flex-1 flex-col gap-[2px] overflow-hidden">
        <div className="truncate text-sm font-semibold text-textPrimary">
          {primary}
        </div>
        {subline && (
          <div className="truncate text-xs text-textMuted">{subline}</div>
        )}
      </div>

      {/* Right column: TAG + status at the rightmost edge, pencil below */}
      <div className="flex shrink-0 flex-col items-end justify-between gap-[2px]">
        <div className="flex items-center gap-xs">
          <span className="shrink-0 rounded-sm bg-surface px-[5px] py-[1px] text-[10px] font-semibold uppercase text-textMuted">
            {TYPE_TAG[kind]}
          </span>
          {statusLabel && (
            <span className={clsx('shrink-0 text-xs font-semibold capitalize', STATUS_TEXT_CLASS[statusKey])}>
              {statusLabel}
            </span>
          )}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            title="Edit"
            aria-label="Edit"
            className="text-textMuted opacity-0 transition-opacity hover:text-textPrimary group-hover:opacity-100 focus:opacity-100"
          >
            ✎
          </button>
        ) : (
          // Placeholder so cards without edit (chambers) keep the same
          // vertical rhythm — TAG/status don't sag into the empty bottom.
          <span aria-hidden="true" />
        )}
      </div>
    </div>
  )
})
EntityCard.displayName = 'EntityCard'
